import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { useIframeCommunication } from '../../hooks/useIframeCommunication';
import Footer from '../../components/Footer';
import { API_BASE_URL } from '../../config';
import { useConfig } from '../../contexts/ConfigContext';
import { getSpecializationTerm, isSpecializationDirection, getSpecializationName } from '../../utils/organizationHelpers';
import { formatDateRangeRu } from '../../utils/dateUtils';
import { displayTrainerName, displayGroupRole } from '../../utils/userDisplayName';
import './GroupPage.css';

const trainerMapKey = (trainer) => {
    const rawId = trainer?.user_id;
    if (rawId !== null && rawId !== undefined && String(rawId).trim() !== '') {
        return `user:${String(rawId).trim()}`;
    }
    return `name:${String(trainer?.name || '').trim().toLowerCase()}`;
};

const GroupPage = () => {
    // Общение с родительским окном через postMessage
    useIframeCommunication();
    const { id } = useParams();
    const { config } = useConfig();
    const orgCode = config?.code || 'mag';

    const { data: group, isLoading: groupLoading, error: groupError } = useQuery(
        ['publicGroup', id],
        () => axios.get(`${API_BASE_URL}/groups/public/${id}`).then(res => res.data)
    );

    const { data: schedule } = useQuery(
        ['publicGroupSchedule', id],
        () => axios.get(`${API_BASE_URL}/schedule/public/group/${id}`).then(res => res.data),
        { enabled: !!group }
    );

    // Проверяем, является ли группа регулярной
    const isRegularGroup = group?.recruitment_status === 'regular';
    const canApply = group?.recruitment_status !== 'closed';

    // Проверяем, является ли это специализацией/авторской программой
    const isSpecialization = isSpecializationDirection(group?.direction_name, orgCode);
    const specializationName = getSpecializationName(group?.direction_name || '', orgCode);
    const specializationTerm = getSpecializationTerm(orgCode);

    if (groupLoading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    if (groupError || !group) {
        return (
            <div className="error-page">
                <h2>Группа не найдена</h2>
                <Link to="/public/groups" className="btn btn-primary">
                    Вернуться к списку групп
                </Link>
            </div>
        );
    }

    // Роли только из раздела "Тренеры" (access_data)
    const accessRoleByUserId = {};
    if (group.access_data && Array.isArray(group.access_data)) {
        group.access_data.forEach(access => {
            if (access.user_id && access.role) {
                accessRoleByUserId[access.user_id] = displayGroupRole(access.role);
            }
        });
    }

    // Кнопка «Назад» всегда видна на первом слайде (слева сверху)

    // Собираем ведущих (менеджер + access_data + тренеры из расписания) без дублей
    // Ключ — user_id, чтобы один человек не повторялся несколько раз
    const trainersMap = new Map();
    const hideManager = Boolean(group.hide_manager_from_trainers);
    const managerId = group.manager_id != null ? String(group.manager_id) : null;

    if (group.manager_name && !hideManager) {
        const key = trainerMapKey({ user_id: group.manager_id, name: group.manager_name });
        trainersMap.set(key, {
            user_id: group.manager_id,
            name: group.manager_name,
            photo_url: group.manager_photo_url,
            short_description: group.manager_short_description || ''
        });
    }

    if (group.access_data && Array.isArray(group.access_data)) {
        group.access_data.forEach(access => {
            if (hideManager && managerId && String(access.user_id) === managerId) return;
            const key = trainerMapKey({ user_id: access.user_id, name: access.name });
            const existing = trainersMap.get(key);
            if (!existing) {
                trainersMap.set(key, {
                    user_id: access.user_id,
                    name: access.name,
                    photo_url: access.photo_url,
                    short_description: access.short_description || ''
                });
            } else if (!existing.short_description && access.short_description) {
                existing.short_description = access.short_description;
            }
        });
    }

    if (schedule && Array.isArray(schedule)) {
        schedule.forEach((event) => {
            if (!event?.trainers || !Array.isArray(event.trainers)) return;
            event.trainers.forEach((trainer) => {
                if (hideManager && managerId && String(trainer.user_id) === managerId) return;
                const key = trainerMapKey(trainer);
                if (!trainersMap.has(key)) {
                    trainersMap.set(key, {
                        user_id: trainer.user_id,
                        name: trainer.name,
                        last_name: trainer.last_name,
                        photo_url: trainer.photo_url,
                        short_description: trainer.short_description || ''
                    });
                } else if (!trainersMap.get(key).short_description && trainer.short_description) {
                    trainersMap.get(key).short_description = trainer.short_description;
                }
            });
        });
    }

    const trainers = Array.from(trainersMap.values());

    return (
        <div className="group-page-landing">
            {/* Секция 1: Название группы */}
            <div className="section w" id="section1">
                <Link to="/public/groups" className="group-page-back-link">
                    <ArrowLeft size={18} />
                    Назад к доступным группам
                </Link>
                <div className="sectionContent cont1">
                    {isSpecialization ? (
                        <>
                            <h1><strong>{specializationTerm.toUpperCase()}</strong></h1>
                            <br />
                            <h2>
                                <b>{specializationName}</b>
                            </h2>
                        </>
                    ) : (
                        <>
                            <br />
                            <h1><strong>{group.direction_full_name || group.nomination}</strong></h1>
                            {group.direction_full_name && group.nomination && (
                                <>
                                    <br />
                                    <h2>{group.nomination}</h2>
                                </>
                            )}
                        </>
                    )}

                    <div className="section1-buttons">
                        {group.direction_page_link ? (
                            <a
                                href={group.direction_page_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-group-landing btn-group-landing-secondary pl-5 pr-5"
                            >
                                Подробнее
                            </a>
                        ) : null}
                        {canApply && (
                            <Link
                                to={`/public/application/${group.id}`}
                                className="btn-group-landing pl-5 pr-5"
                            >
                                принять участие
                            </Link>
                        )}
                        {orgCode === 'nasledie' && (
                            <a
                                href="https://nasledietz.ru/payment"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-group-landing btn-group-landing-secondary pl-5 pr-5"
                            >
                                Оплата
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Секция 2: Формат и стоимость */}
            <div className="section b" id="section4">
                <div className="sectionContent format-price-section">
                    <div className="miniHeader">ФОРМАТ и СТОИМОСТЬ</div>

                    <div className="format-price-grid">
                        <div className="format-price-block format-price-place">
                            <div className="format-price-block-label">Место проведения</div>
                            <div className="format-price-block-value">{group.region_name || 'Не указано'}</div>
                        </div>

                        {group.format_data && group.format_data.length > 0 && (
                            <>
                                <div className="format-price-block format-price-frequency">
                                    <div className="format-price-block-label">Режим встреч</div>
                                    <div className="format-price-block-value format-price-value-large">
                                        {group.format_data[0]?.frequency === 'weekly' ? 'Раз в неделю (регулярно)' :
                                            group.format_data[0]?.frequency === 'biweekly' ? 'Раз в 2 недели (регулярно)' : 'По расписанию'}
                                    </div>
                                </div>
                                <div className="format-price-block format-price-schedule">
                                    <div className="format-price-block-label">Дни и время</div>
                                    <div className="format-price-block-value">
                                        {group.format_data.map((f, idx) => (
                                            <span key={idx} className="format-price-schedule-item">
                                                {f.weekDay}{f.timeFrom && f.timeTo ? ` с ${f.timeFrom} до ${f.timeTo}` : ''}
                                                {idx < group.format_data.length - 1 ? '; ' : ''}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        {(group.price_per_cycle > 0 || group.price_full_course > 0) && (
                            <div className="format-price-block format-price-prices">
                                <div className="format-price-block-label">Стоимость</div>
                                <div className="format-price-block-value format-price-prices-inner">
                                    {group.price_per_cycle > 0 && (
                                        <div className="format-price-price-line">
                                            <span className="format-price-price-label">За цикл</span>
                                            <span className="format-price-price-value">{Number(group.price_per_cycle).toLocaleString('ru-RU')} ₽</span>
                                        </div>
                                    )}
                                    {group.price_full_course > 0 && (
                                        <div className="format-price-price-line">
                                            <span className="format-price-price-label">За курс</span>
                                            <span className="format-price-price-value">{Number(group.price_full_course).toLocaleString('ru-RU')} ₽</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {canApply && (
                        <Link
                            to={`/public/application/${group.id}`}
                            className="btn-group-landing pl-5 pr-5"
                        >
                            принять участие
                        </Link>
                    )}
                </div>
            </div>

            {/* Секция 3: Расписание — список встреч или формат (если регулярно и даты прошли) */}
            {!isRegularGroup && (() => {
                const hasRegularFormat = group?.format_data?.[0]?.frequency === 'weekly' || group?.format_data?.[0]?.frequency === 'biweekly';
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const lastDatePassed = group?.latest_event_date && (() => {
                    const d = new Date(group.latest_event_date);
                    d.setHours(0, 0, 0, 0);
                    return d < today;
                })();
                const showFormatInsteadOfSchedule = hasRegularFormat && (lastDatePassed || !schedule?.length);
                if (showFormatInsteadOfSchedule && group?.format_data?.length > 0) {
                    return (
                        <div className="section w" id="section5">
                            <div className="sectionContent">
                                <div className="miniHeader">Расписание</div>
                                <p className="format-as-schedule">
                                    {group.format_data[0]?.frequency === 'weekly' ? 'Раз в неделю (регулярно)' :
                                        group.format_data[0]?.frequency === 'biweekly' ? 'Раз в 2 недели (регулярно)' : 'По расписанию'}
                                </p>
                                <p>
                                    {group.format_data.map((f, idx) => (
                                        <span key={idx}>
                                            {f.weekDay}{f.timeFrom && f.timeTo ? ` с ${f.timeFrom} до ${f.timeTo}` : ''}
                                            {idx < group.format_data.length - 1 ? '; ' : ''}
                                        </span>
                                    ))}
                                </p>
                                {canApply && (
                                    <Link to={`/public/application/${group.id}`} className="btn-group-landing pl-5 pr-5">принять участие</Link>
                                )}
                            </div>
                        </div>
                    );
                }
                if (schedule && schedule.length > 0) {
                    return (
                        <div className="section w" id="section5">
                            <div className="sectionContent">
                                <div className="miniHeader">Расписание</div>
                                {schedule.map(event => (
                                    <div key={event.id} className="line">
                                        <div className="dateSectionT">
                                            <div className="dateSection1">
                                                <p>{event.theme}</p>
                                                {event.trainers && event.trainers.length > 0 && (
                                                    <p className="trainers-list">
                                                        {event.trainers.map((trainer, idx) => (
                                                            <span key={idx}>
                                                                {displayTrainerName(trainer)}
                                                                {accessRoleByUserId[trainer.user_id] ? ` (${accessRoleByUserId[trainer.user_id]})` : ''}
                                                                {idx < event.trainers.length - 1 ? ', ' : ''}
                                                            </span>
                                                        ))}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="dateSection2T">
                                                <p>{formatDateRangeRu(event.start_date, event.end_date)}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {canApply && (
                                    <Link
                                        to={`/public/application/${group.id}`}
                                        className="btn-group-landing pl-5 pr-5"
                                    >
                                        принять участие
                                    </Link>
                                )}
                            </div>
                        </div>
                    );
                }
                return null;
            })()}

            {/* Секция 4: О группе (только если заполнено в редактировании группы) */}
            {group.about && group.about.trim() && (
                <div className="section b" id="section2">
                    <div className="sectionContent">
                        <div className="miniHeader">О группе</div>
                        <div className="about-group-text">
                            {group.about.split('\n').map((line, idx) => (
                                <p key={idx}>{line || '\u00A0'}</p>
                            ))}
                        </div>
                        {canApply && (
                            <Link
                                to={`/public/application/${group.id}`}
                                className="btn-group-landing pl-5 pr-5"
                            >
                                принять участие
                            </Link>
                        )}
                    </div>
                </div>
            )}

            {/* Секция 5: Ведущие */}
            {trainers.length > 0 && (
                <div className="section w" id="section6">
                    <div className="sectionContent">
                        <div className="miniHeader">ВЕДУЩИЕ</div>
                        {trainers.map((trainer, idx) => (
                            <div key={idx} className="dateSection" id="vedselection">
                                <div className="dateSection12">
                                    {trainer.user_id ? (
                                        <Link to={orgCode === 'nasledie' ? `/public/profile/${trainer.user_id}` : `/public/teacher/${trainer.user_id}`} target="_blank">
                                            {trainer.photo_url ? (
                                                <img className="trenerphoto clickable-photo" src={trainer.photo_url} alt={displayTrainerName(trainer)} />
                                            ) : (
                                                <div className="trenerphoto trenerphoto-placeholder clickable-photo">
                                                    {displayTrainerName(trainer)?.charAt(0) || '?'}
                                                </div>
                                            )}
                                        </Link>
                                    ) : (
                                        <>
                                            {trainer.photo_url ? (
                                                <img className="trenerphoto" src={trainer.photo_url} alt={displayTrainerName(trainer)} />
                                            ) : (
                                                <div className="trenerphoto trenerphoto-placeholder">
                                                    {displayTrainerName(trainer)?.charAt(0) || '?'}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className="ved-block">
                                    <p>
                                        <b>{displayTrainerName(trainer)}</b>
                                        {(() => {
                                            const accessRole = accessRoleByUserId[trainer.user_id];
                                            const managerFallbackRole = trainer.user_id === group.manager_id ? 'Преподаватель' : null;
                                            const roleToShow = accessRole || managerFallbackRole;
                                            return roleToShow ? ` - ${roleToShow}` : '';
                                        })()}
                                    </p>
                                    {trainer.short_description && (
                                        <div className="ved-text">{trainer.short_description}</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <Footer />
        </div>
    );
};

export default GroupPage;
